
  import { defineComponent, ref, computed } from 'vue';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { getCSSVariableValue } from '@/assets/ts/_utils';
  import { useI18n } from 'vue-i18n/index';
  import {
    pct,
    textColor,
    arrowSrc,
    arrow,
  } from '@/core/helpers/dashboard-utils';
  import CustomDatePicker from '@/components/search/CustomDatePicker.vue';

  export default defineComponent({
    name: 'subscriptions-report-widget',
    props: {
      widgetClasses: String,
    },
    components: {
      CustomDatePicker,
    },
    async setup() {
      const { t, te } = useI18n();
      const loading = ref(true);
      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };
      const selectedPeriod = ref('last-7-days');
      const customDateFrom = ref('');
      const customDateTo = ref('');
      const showDatePicker = ref(false);
      interface SubscriptionData {
        packageType: string;
        title: string;
        count: number;
        revenues: number;
        revenuesChangePercentage: number;
      }

      const arrayOfData = ref<SubscriptionData[]>([]);
      const total = ref<SubscriptionData>();

      const store = useStore();

      try {
        await store.dispatch(Actions.GET_BROKERS_ADS_PACKAGES_DASHBOARD);
        const data = computed(() => store.getters.brokersAdsPackagesDashboard);

        if (
          data.value &&
          Array.isArray(data.value.data) &&
          data.value.data.length > 0
        ) {
          arrayOfData.value = data.value.data.slice(0, -1);
          total.value = data.value.data.slice(-1)[0];
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        loading.value = false;
      }

      const showCustomDatePicker = () => {
        showDatePicker.value = true;
        selectedPeriod.value = 'custom';
      };

      const applyCustomDates = async () => {
        if (!customDateFrom.value || !customDateTo.value) {
          alert('Please select both dates');
          return;
        }
        showDatePicker.value = false;
        await selectPeriod('custom');
      };

      const selectPeriod = async (period: string) => {
        loading.value = true;
        selectedPeriod.value = period;
        const obj = {
          dateRange: period,
          showTrend: false,
          dateFrom: '',
          dateTo: '',
        };

        if (period === 'custom' && customDateFrom.value && customDateTo.value) {
          obj.dateFrom = customDateFrom.value;
          obj.dateTo = customDateTo.value;
        }

        try {
          await store.dispatch(Actions.GET_BROKERS_ADS_PACKAGES_DASHBOARD, obj);
          const payload = store.getters.brokersAdsPackagesDashboard;

          if (
            payload?.data &&
            Array.isArray(payload.data) &&
            payload.data.length > 0
          ) {
            arrayOfData.value = payload.data.slice(0, -1);
            total.value = payload.data.slice(-1)[0];
          }
        } catch (error) {
          console.error('Error loading period data:', error);
        } finally {
          loading.value = false;
        }
      };

      // Chart colors
      const primaryColor = getCSSVariableValue('--bs-primary');
      const warningColor = getCSSVariableValue('--bs-warning');
      const infoColor = getCSSVariableValue('--bs-info');

      const getClassBg = (packageType) => {
        switch (packageType) {
          case 'pro-broker-pack':
            return 'bg-primary';
          case 'special-pack':
            return 'bg-warning';
          case 'saving-pack':
            return 'bg-info';
          case 'starter-pack':
            return 'bg-secondary';
          case 'special-ads-one-time':
            return 'bg-success';
          case 'ads-one-time':
            return 'bg-danger';
          default:
            return 'bg-dark';
        }
      };

      const hasData = computed(() => {
        return (
          arrayOfData.value &&
          arrayOfData.value.length > 0 &&
          arrayOfData.value.some((item) => item.count > 0)
        );
      });

      const chartSeries = computed(() => {
        if (!hasData.value) {
          return [1];
        }
        return arrayOfData.value.map((element) => element.count);
      });

      const chartOptions = computed(() => ({
        chart: {
          fontFamily: 'inherit',
          type: 'donut',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: false,
              },
            },
          },
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 2,
        },
        colors: hasData.value
          ? [
              primaryColor,
              warningColor,
              infoColor,
              '#6610f2',
              '#fd7e14',
              '#20c997',
            ]
          : ['#E4E6EF'],
        labels: hasData.value
          ? arrayOfData.value.map((item) => item.title)
          : ['No Data'],
        tooltip: {
          enabled: hasData.value,
          style: {
            fontSize: '12px',
          },
          y: {
            formatter: function (val: number) {
              return val + '';
            },
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 180,
              },
            },
          },
        ],
      }));

      return {
        getClassBg,
        total,
        arrayOfData,
        loading,
        chartOptions,
        chartSeries,
        selectedPeriod,
        selectPeriod,
        translate,
        pct,
        textColor,
        arrowSrc,
        arrow,
        showDatePicker,
        customDateFrom,
        customDateTo,
        showCustomDatePicker,
        applyCustomDates,
      };
    },
  });
